/**
 * Streama CMS · IAM API client
 *
 * Typed wrappers over the deployed Sprint 1 IAM surface plus the
 * one new endpoint the backend team is adding for Batch A:
 *
 *   AuthController:
 *     POST  /api/cms/users/login          [public]
 *     GET   /api/cms/users/profile        [authenticated]
 *     PATCH /api/cms/users/password       [authenticated, change own]
 *
 *   UserController:
 *     GET    /api/cms/users               [PRIV_users]
 *     GET    /api/cms/users/{userName}    [PRIV_users]
 *     POST   /api/cms/users               [PRIV_add_users]
 *     PATCH  /api/cms/users/{userName}    [PRIV_users]
 *     DELETE /api/cms/users/{userName}    [PRIV_delete_users]
 *     PATCH  /api/cms/users/{userName}/password    [PRIV_reset_user_password]
 *     GET    /api/cms/users/{userName}/permissions [PRIV_users]
 *     PATCH  /api/cms/users/{userName}/permissions [PRIV_user_group]
 *
 *   NEW for Batch A (backend team adding):
 *     GET    /api/cms/users/groups        [PRIV_users]
 *
 * Privileges are passed on the wire as bare names. The PRIV_ prefix
 * is internal to Spring Security's authority matching only.
 *
 * Every method returns the unwrapped data — the axios interceptor in
 * api/client.ts handles the {data, message} envelope unwrap on success
 * and converts {status, error, message, fieldErrors} into ApiException
 * on failure.
 */
import { apiClient } from '@/api/client'
import type {
  UserCreateInput,
  UserUpdateInput,
  PasswordChangeInput,
  PasswordResetInput,
  PermissionsUpdateInput,
} from './schemas'

/**
 * Returned by `GET /api/cms/users` (list endpoint). Mirrors the
 * deployed `IamDtos.UserView` record exactly. This is the row shape
 * the table renders.
 */
export interface UserView {
  userName: string
  owner: string | null
  external: boolean
  /** ISO-8601 LocalDateTime, e.g. "2024-08-12T14:30:00". */
  created: string
  /** ISO-8601 LocalDateTime, may equal created if never updated. */
  modified: string
  /** Legacy CakePHP financial fields — hidden by default in the table. */
  tax: number | null
  minGuarantee: number | null
  withholdingTax: number | null
  detailedReport: number | null
  approve: boolean | null
}

/**
 * Returned by `GET /api/cms/users/profile`. This is the user-summary
 * shape the auth store also receives at login. Privileges arrive as
 * bare names (no PRIV_ prefix).
 */
export interface UserSummary {
  userName: string
  external: boolean
  privileges: string[]
}

/**
 * Returned by `GET /api/cms/users/{userName}/permissions`. The map
 * has every privilege the system knows about as a key, with the
 * boolean indicating whether this user has it.
 */
export interface PermissionsView {
  userName: string
  permissions: Record<string, boolean>
}

/**
 * Returned by `GET /api/cms/users/groups` (new endpoint).
 *
 * `memberCount` is informational — useful for the create-user drawer's
 * group picker so admins can see "Standard admins (12 members)" rather
 * than just a name. If the backend doesn't include it in the first
 * cut, treat it as optional.
 */
export interface UserGroupSummary {
  id: number
  name: string
  memberCount?: number
}

// -----------------------------------------------------------------
// API surface
// -----------------------------------------------------------------

export const userApi = {
  /** List all CMS admins. Currently unpaginated — admin set is small. */
  async list(): Promise<UserView[]> {
    const res = await apiClient.get<UserView[]>('/cms/users')
    return res.data
  },

  /** Fetch one admin by userName. */
  async get(userName: string): Promise<UserView> {
    const res = await apiClient.get<UserView>(
      `/cms/users/${encodeURIComponent(userName)}`,
    )
    return res.data
  },

  /**
   * Create a new admin. Backend assigns id, sets created/modified,
   * stores password as bcrypt(12). Returns the newly-created UserView.
   *
   * Throws ApiException with code='VALIDATION' and fieldErrors set if
   * the request body fails Bean Validation. Throws code='CONFLICT' if
   * userName is already taken.
   */
  async create(input: UserCreateInput): Promise<UserView> {
    const res = await apiClient.post<UserView>('/cms/users', input)
    return res.data
  },

  /**
   * Update an admin's mutable fields. Note that the backend's
   * UserUpdateRequest covers `external` and the 5 financial integers
   * — it does NOT permit changing userName. To rename a user, delete
   * and recreate.
   */
  async update(userName: string, input: UserUpdateInput): Promise<UserView> {
    const res = await apiClient.patch<UserView>(
      `/cms/users/${encodeURIComponent(userName)}`,
      input,
    )
    return res.data
  },

  /**
   * Delete an admin. The backend may soft-delete (active=false) or
   * hard-delete depending on config — caller doesn't need to know.
   * Returns void.
   */
  async delete(userName: string): Promise<void> {
    await apiClient.delete(`/cms/users/${encodeURIComponent(userName)}`)
  },

  /**
   * Admin-side password reset (different from change-my-own-password).
   * No current-password check; admin is taking the user's word for it.
   * Requires PRIV_reset_user_password.
   */
  async resetPassword(
    userName: string,
    input: PasswordResetInput,
  ): Promise<void> {
    await apiClient.patch(
      `/cms/users/${encodeURIComponent(userName)}/password`,
      input,
    )
  },

  /**
   * Fetch every privilege known to the system, with a boolean per
   * privilege indicating whether {userName} has it. The map is
   * deterministic — keys ordered by privilege name on the backend
   * but the SPA treats the order as undefined and groups them
   * domain-wise on the client.
   */
  async getPermissions(userName: string): Promise<PermissionsView> {
    const res = await apiClient.get<PermissionsView>(
      `/cms/users/${encodeURIComponent(userName)}/permissions`,
    )
    return res.data
  },

  /**
   * Replace {userName}'s privilege set. Backend computes the diff;
   * the SPA submits the full target state. Requires PRIV_user_group.
   *
   * If the userName matches the currently logged-in admin, callers
   * should also invalidate the profile query so route guards see the
   * change without re-login.
   */
  async updatePermissions(
    userName: string,
    input: PermissionsUpdateInput,
  ): Promise<void> {
    await apiClient.patch(
      `/cms/users/${encodeURIComponent(userName)}/permissions`,
      input,
    )
  },

  /**
   * List all CMS user groups. Required to populate the create-user
   * drawer's group picker.
   *
   * Backend status: NEW endpoint added for Batch A. If the backend
   * deploy hasn't landed when this code runs, calls return 404 and
   * the create-user drawer surfaces an empty-state.
   */
  async listGroups(): Promise<UserGroupSummary[]> {
    const res = await apiClient.get<UserGroupSummary[]>('/cms/users/groups')
    return res.data
  },
}

// -----------------------------------------------------------------
// Auth-controller wrappers (current-user actions)
// -----------------------------------------------------------------

export const accountApi = {
  /** Fetch the current user's summary including bare-name privileges. */
  async profile(): Promise<UserSummary> {
    const res = await apiClient.get<UserSummary>('/cms/users/profile')
    return res.data
  },

  /**
   * Change the current user's password. Requires the current password
   * for confirmation. The backend rotates the legacy MD5 hash to
   * bcrypt as a side effect (lazy migration).
   */
  async changeMyPassword(input: PasswordChangeInput): Promise<void> {
    await apiClient.patch('/cms/users/password', input)
  },
}
